import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, X, Loader2, Trophy, RefreshCw, FileSpreadsheet, FileDown } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";

// Portfolio HMS Ranking: ranks every completed licence area by the strength and size of its
// coastal heavy-mineral-sands corridors (indicative HM tonnage + corridor strength index).
export default function HMSRankingDialog({ open, onOpenChange, projectId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError(false);
    setData(null);
    try {
      const { data: d } = await api.get(`/projects/${projectId}/hms-ranking`);
      if (!signal?.cancelled) setData(d);
    } catch (e) {
      if (!signal?.cancelled) {
        setError(true);
        toast.error(apiError(e));
      }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!open) return undefined;
    const signal = { cancelled: false };
    load(signal);
    return () => { signal.cancelled = true; };
  }, [open, load]);

  const download = async (fmt) => {
    const tid = toast.loading(`Preparing ${fmt.toUpperCase()} export…`);
    try {
      const r = await api.get(`/projects/${projectId}/hms-ranking/export?fmt=${fmt}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = `SYMIN_HMS_Ranking.${fmt === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
      toast.success("Ranking exported", { id: tid });
    } catch (e) {
      toast.error(apiError(e), { id: tid });
    }
  };

  const rows = data?.areas || [];
  const asmp = data?.assumptions;
  const chart = rows.map((r) => ({
    name: r.name ? String(r.name).slice(0, 22) : r.area_id,
    mid: +(((r.hm_mt_low || 0) + (r.hm_mt_high || 0)) / 2).toFixed(3),
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[92vw] w-[92vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden [&>button]:hidden"
        data-testid="hms-ranking-dialog"
      >
        <div className="flex items-center justify-between px-5 h-14 border-b border-black/20 shrink-0" style={{ background: "#0B1420" }}>
          <DialogTitle className="text-sm font-bold flex items-center gap-2" style={{ color: "#FFFFFF", fontFamily: "Manrope, sans-serif" }}>
            <BarChart3 size={16} /> Portfolio HMS Ranking
          </DialogTitle>
          <DialogDescription className="sr-only">
            Licence areas ranked by the strength and size of their interpreted coastal heavy-mineral-sands corridors.
          </DialogDescription>
          <div className="flex items-center gap-2">
            <button data-testid="hms-export-csv" onClick={() => download("csv")} disabled={!rows.length}
              className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 bg-white/10 text-white hover:bg-white/20 disabled:opacity-40">
              <FileDown size={13} /> CSV
            </button>
            <button data-testid="hms-export-xlsx" onClick={() => download("xlsx")} disabled={!rows.length}
              className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40" style={{ background: "#C20511", color: "#FFFFFF" }}>
              <FileSpreadsheet size={13} /> Excel
            </button>
            <button data-testid="hms-ranking-close" onClick={() => onOpenChange(false)} className="h-8 w-8 grid place-items-center rounded-md text-slate-300 hover:bg-white/10 ml-1">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 p-4 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center gap-2 text-slate-500 py-24">
              <Loader2 className="animate-spin" />
              <span className="text-sm">Ranking coastal HMS corridors across the portfolio…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 text-slate-500 py-24" data-testid="hms-ranking-error">
              <span className="text-sm">Could not load the portfolio HMS ranking.</span>
              <button onClick={() => load()} data-testid="hms-ranking-retry"
                className="h-9 px-4 rounded-md text-xs font-semibold flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800">
                <RefreshCw size={13} /> Retry
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-500 py-24 text-center" data-testid="hms-ranking-empty">
              No coastal HMS corridors resolved across the completed areas yet.
            </div>
          ) : (
            <>
              {data?.leader && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid="hms-ranking-leader">
                  <span className="h-10 w-10 rounded-md grid place-items-center shrink-0" style={{ background: "#FDECEC", color: "#92400E" }}><Trophy size={18} /></span>
                  <div className="text-sm text-slate-700">
                    <b>{data.leader.name || data.leader.area_id}</b> leads the portfolio - {data.leader.n_corridors} corridor(s), ~{data.leader.total_length_km} km total, indicative <b>{data.leader.hm_mt_low}–{data.leader.hm_mt_high} Mt</b> heavy mineral.
                  </div>
                </div>
              )}
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="hms-ranking-chart">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Indicative Heavy-Mineral Tonnage (Mt, midpoint)</h3>
                <ResponsiveContainer width="100%" height={Math.max(160, chart.length * 46)}>
                  <BarChart data={chart} layout="vertical" margin={{ left: 20, right: 40, top: 5, bottom: 5 }}>
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#475569" }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: "#0B1420" }} />
                    <Tooltip formatter={(v) => [`${v} Mt`, "Indic. HM"]} />
                    <Bar dataKey="mid" radius={[0, 4, 4, 0]}>
                      {chart.map((_, i) => <Cell key={i} fill={i === 0 ? "#C20511" : "#2A6F97"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="hms-ranking-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left" style={{ background: "#0B1420", color: "#fff" }}>
                      {["#", "Area", "Licence", "Corridors", "Total km", "Prim. W km", "HMS Score", "Indic. HM (Mt)", "Index"].map((h) => (
                        <th key={h} className="px-3 py-2 text-xs font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.area_id} className="border-t border-slate-100" data-testid={`hms-rank-row-${r.area_id}`}>
                        <td className="px-3 py-2 font-bold text-slate-900">{r.rank}</td>
                        <td className="px-3 py-2">{r.area_id}</td>
                        <td className="px-3 py-2 text-slate-700">{r.name}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{r.n_corridors}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{r.total_length_km}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{r.primary_width_km}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{r.mean_score}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{r.hm_mt_low}–{r.hm_mt_high}</td>
                        <td className="px-3 py-2 text-center font-bold" style={{ color: "#C20511" }}>{r.index}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400 leading-snug px-1">
                {asmp ? `Assumptions: thickness ${asmp.thickness_low_m}–${asmp.thickness_high_m} m · density ${asmp.bulk_density} t/m³. ` : ""}
                Tonnages are conceptual, exploration-stage order-of-magnitude figures for target prioritisation only - not a Mineral Resource.
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
